<template>
  <div>
    <h1 class="heading-1 mb-24">{{ i18n(translations.title) }}</h1>
    <p class="body-1 mb-24">{{ i18n(translations.description) }}</p>

    <div class="mb-36">
      <RevCheckbox
        id="same_as_account_requester"
        v-model="isSameAsAccountRequester"
        @change="
          (value) => handleCheckSameAsAccountRequester(value.target.checked)
        "
      >
        <template #label>
          {{ i18n(translations.sameAsAccountRequesterLabel) }}
        </template>
      </RevCheckbox>
    </div>

    <StakeholderForm
      :description="i18n(translations.description)"
      :disabled="isFormDisabled"
      :feature-code="FeatureCode.WEB_KYB_BUSINESS_DETAILS"
      :form-id="FORM_ID"
      :stakeholder-data="legalRepresentative"
      :title="i18n(translations.title)"
      @update:model-value="handleCheckSameAsAccountRequester"
    />

    <!-- Save Button -->
    <div
      class="mx-auto mt-48 flex w-[328px] items-center justify-center gap-16"
    >
      <RevButton
        class="w-full"
        :form="FORM_ID"
        type="submit"
        variant="primary"
        @click="handleSubmit"
      >
        {{ i18n(translations.saveButton) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import { resetForm, setFormValues } from '@ds/components/Form/Form.actions'

import type { KybData } from '~/scopes/kyb/pages/Index.types'

import StakeholderForm from '../components/StakeholderForm/StakeholderForm.vue'

import translations from './LegalRepresentative.translations'

const props = defineProps<{
  kybData: KybData
}>()

const i18n = useI18n()
const FORM_ID = 'kyb_legal_representative_form'

const isFormDisabled = ref(false)

const legalRepresentative = computed(() => props.kybData?.legalRepresentative)
const isSameAsAccountRequester = ref(
  props.kybData?.legalRepresentative?.sameAsAccountRequester ?? false,
)

// TODO: Implement form submission logic
async function handleSubmit() {
  try {
    // eslint-disable-next-line no-console
    console.log('Legal Representative form submitted:')
    // eslint-disable-next-line no-console
    console.log('Same as account requester:', isSameAsAccountRequester.value)
  } catch (error) {
    // console.error('Form submission error:', error)
  }
}

function handleCheckSameAsAccountRequester(value: boolean) {
  if (value === true) {
    isFormDisabled.value = true
    setFormValues(FORM_ID, {
      firstName: props.kybData.accountRequester.firstName,
      kanaFirstName: props.kybData.accountRequester.kanaFirstName,
      latinFirstName: props.kybData.accountRequester.latinFirstName,
      lastName: props.kybData.accountRequester.lastName,
      kanaLastName: props.kybData.accountRequester.kanaLastName,
      latinLastName: props.kybData.accountRequester.latinLastName,
      dateOfBirth: props.kybData.accountRequester.dateOfBirth,
      countryOfCitizenship: props.kybData.accountRequester.countryOfCitizenship,
      phoneNumber: props.kybData.accountRequester.phoneNumber,
      countryOfResidency: props.kybData.accountRequester.countryOfResidency,
      addressLine1: props.kybData.accountRequester.address?.addressLine1,
      addressLine2: props.kybData.accountRequester.address?.addressLine2,
      postalCode: props.kybData.accountRequester.address?.postalCode,
      city: props.kybData.accountRequester.address?.city,
      country: props.kybData.accountRequester.address?.country,
      identificationType:
        props.kybData.accountRequester.identityDocument?.identificationType,
      countryOfIssue:
        props.kybData.accountRequester.identityDocument?.countryOfIssue,
      photoIdFront: props.kybData.accountRequester.identityDocument?.front,
      photoIdBack: props.kybData.accountRequester.identityDocument?.back,
    })
  } else {
    isFormDisabled.value = false

    resetForm(FORM_ID)
  }
}
</script>
